import { fetchApi } from '../../api/client';
import type { FieldValue } from './formScope';

/**
 * Where a merchant's own form sends its answers.
 *
 * `POST /api/v1/form-submissions` is this platform's endpoint for a builder-built form — tenant
 * scoped by the storefront's own host, rate limited, and stored under the field names the merchant
 * chose. The server's schema is the authority on size: field names, value lengths and the field
 * count are all bounded there, and this file sends exactly what the form validated — never more.
 */

/** One submission as the endpoint takes it: which form, on which page, and the checked answers. */
export type FormSubmissionPayload = {
  formId: string;
  formName: string;
  page: string;
  values: Record<string, string | boolean | string[]>;
};

// Copies the answers into the wire shape, thawing the read-only file lists the form scope holds.
function wireValues(values: Readonly<Record<string, FieldValue>>): Record<string, string | boolean | string[]> {
  const bag: Record<string, string | boolean | string[]> = {};
  for (const [name, value] of Object.entries(values)) bag[name] = typeof value === 'object' ? [...value] : value;
  return bag;
}

/** Names the storefront page a submission came from, so one form on two pages stays tellable apart. */
export function submissionPage(): string {
  return typeof window === 'undefined' ? '' : window.location.pathname.slice(0, 512);
}

// Sends one checked submission to the platform's own endpoint; the caller decides what a refusal shows.
export async function sendFormSubmission(
  formId: string,
  formName: string,
  values: Readonly<Record<string, FieldValue>>
): Promise<void> {
  const payload: FormSubmissionPayload = { formId, formName, page: submissionPage(), values: wireValues(values) };
  await fetchApi<unknown>('/form-submissions', { method: 'POST', body: JSON.stringify(payload) });
}
